// src/components/resources/glossary/GlossaryCategoryContent.tsx

'use client'

import { useState, useCallback } from 'react'
import { GlossaryFilters } from '~/components/resources/glossary/GlossaryFilters'
import { GlossaryTermGrid } from '~/components/resources/glossary/GlossaryTermGrid'
import { GlossaryEmptyState } from '~/components/resources/glossary/GlossaryEmptyState'
import Link from 'next/link'
import { GLOSSARY_CATEGORIES } from '~/lib/resources/types/shared/enums'
import type { GlossaryCategory } from '~/lib/resources/types/shared/enums'
import type { GlossaryTerm } from '~/lib/resources/types/glossary/terms'

interface GlossaryCategoryContentProps {
  category: {
    id: GlossaryCategory
    label: string
    description: string
    icon: string
  }
  initialTerms: GlossaryTerm[]
}

export function GlossaryCategoryContent({ category, initialTerms }: GlossaryCategoryContentProps): JSX.Element {
  const [terms] = useState(initialTerms)
  const [filteredTerms, setFilteredTerms] = useState(initialTerms)
  const [hasActiveFilters, setHasActiveFilters] = useState(false)
  // Bumping this key remounts GlossaryFilters, clearing its internal search /
  // tag state without this parent needing to know the filter internals.
  const [filtersResetKey, setFiltersResetKey] = useState(0)

  const handleFilter = useCallback((filtered: GlossaryTerm[]) => {
    setFilteredTerms(filtered)
    setHasActiveFilters(filtered.length !== terms.length)
  }, [terms.length])

  const handleClearFilters = useCallback(() => {
    setFilteredTerms(terms)
    setHasActiveFilters(false)
    setFiltersResetKey(key => key + 1)
  }, [terms])

  return (
    <div className="space-y-12">
      {/* Filters */}
      <GlossaryFilters
        key={filtersResetKey}
        category={category}
        terms={terms}
        onFilter={handleFilter}
      />

      {/* Terms Grid */}
      {filteredTerms.length > 0 ? (
        <GlossaryTermGrid terms={filteredTerms} />
      ) : (
        <GlossaryEmptyState
          category={category}
          hasFilters={hasActiveFilters}
          onClearFilters={handleClearFilters}
        />
      )}

      {/* Related Categories */}
      <section className="mt-16">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-8">
          Other Categories
        </h2>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {GLOSSARY_CATEGORIES
            .filter(cat => cat.id !== category.id)
            .map(cat => (
              <Link
                key={cat.id}
                href={`/resources/glossary/${cat.id}`}
                className="group relative flex flex-col gap-4 rounded-2xl bg-white p-6 shadow-lg ring-1 ring-gray-200 transition-all hover:shadow-xl dark:bg-gray-800/50 dark:ring-gray-800 dark:hover:bg-gray-800"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{cat.icon}</span>
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-600 dark:text-white dark:group-hover:text-indigo-400">
                    {cat.label}
                  </h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {cat.description}
                </p>
              </Link>
            ))}
        </div>
      </section>
    </div>
  )
} 